import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  updateProfile,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db, functions } from '../services/firebase';
import { deleteAccountCascade } from '../utils/accountDeletion';
import {
  X, User as UserIcon, Camera, Trash2,
  ArrowRight, Save, UserX, Mail, Key, Edit2 } from 'lucide-react';
import { LocationSearch } from './LocationSearch';
import { LazyCropper } from './LazyCropper';
import { getCroppedImg } from '../utils/imageUtils';
import { User } from '../types';
import { FIELD_AUTH, fieldBorder } from './UI';
import { userMessage } from '../utils/userMessage';

interface SettingsModalProps {
  user: User;
  onClose: () => void;
  onUpdate: (user: User) => void;
}

export function SettingsModal({ user, onClose, onUpdate }: SettingsModalProps) {
  const [name, setName] = useState(user.name);
  const [homeLocation, setHomeLocation] = useState(user.homeLocation || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  // Password step for account deletion — see handleDeleteAccount for why
  // this runs BEFORE the destructive cascade rather than after it fails.
  const [awaitingDeletePassword, setAwaitingDeletePassword] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showValidationNames, setShowValidationNames] = useState<string[]>([]);

  // Profile Photo State
  const [image, setImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(user.imgSrc);
  const [isCropping, setIsCropping] = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result as string);
        setIsCropping(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const showCroppedImage = useCallback(async () => {
    try {
      if (image && croppedAreaPixels) {
        const cropped = await getCroppedImg(image, croppedAreaPixels);
        setCroppedImage(cropped);
        setIsCropping(false);
      }
    } catch (e) {
      console.error(e);
    }
  }, [image, croppedAreaPixels]);

  const handleSaveProfile = async () => {
    if (!auth.currentUser) return;
    setShowValidationNames([]);
    
    const missing = [];
    if (!name.trim()) missing.push('name');
    if (!homeLocation.trim()) missing.push('location');
    
    if (missing.length > 0) {
      setShowValidationNames(missing);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // 1. Handle Profile Update
      const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
      const updatedData = {
        name,
        homeLocation,
        imgSrc: croppedImage,
        initials,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'users', auth.currentUser.uid), updatedData);

      await updateProfile(auth.currentUser, {
        displayName: name
        // We skip photoURL here to avoid "Photo URL too long" errors with base64 strings.
        // The image is already saved in Firestore and will be loaded from there.
      });

      const updatedUser: User = {
        ...user,
        ...updatedData
      };

      onUpdate(updatedUser);
      setSuccess('Profile updated successfully!');
    } catch (err: any) {
      if (err.code === 'auth/requires-recent-login') {
        setError('For security, modifying sensitive information requires a recent login. Please sign out and back in.');
      } else {
        setError(userMessage(err, 'Could not update your profile. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!auth.currentUser || !auth.currentUser.email) return;
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const sendReset = httpsCallable(functions, 'sendPasswordResetEmail');
      await sendReset({ email: auth.currentUser.email });
      setSuccess('A password reset link has been sent to your email.');
    } catch (err: any) {
      setError(userMessage(err, 'Could not send the reset link. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Reauthenticates BEFORE deleteAccountCascade runs, not after deleteUser()
  // fails — that ordering is the whole point. The cascade is irreversible and
  // ran first; if the later deleteUser() call then hit Firebase's
  // requires-recent-login (any session older than a few minutes — an
  // everyday state for a PWA people stay signed into for weeks), the user
  // was left with all their data gone and a still-live login, told to "sign
  // out and back in" as if nothing had happened yet.
  //
  // The one alternative — reauthenticating only reactively, after that same
  // catch — was rejected: by the time you'd know reauth was needed, the
  // cascade would already have run. Confirming identity has to happen while
  // there is still something to protect, which means every deletion pays the
  // one extra step now rather than only the unlucky stale-session ones
  // paying with their data.
  const handleDeleteAccount = async () => {
    const current = auth.currentUser;
    if (!current || !current.email) return;
    setLoading(true);
    setError(null);

    try {
      await reauthenticateWithCredential(current, EmailAuthProvider.credential(current.email, deletePassword));
    } catch (err: any) {
      setError(err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password'
        ? 'That password doesn\'t match your account. Nothing has been deleted.'
        : userMessage(err, 'Could not verify your password. Nothing has been deleted.'));
      setLoading(false);
      return;
    }

    try {
      await deleteAccountCascade(current.uid, current.email);
      await deleteUser(current);
      window.location.reload();
    } catch (err: any) {
      // Reauth just succeeded, so requires-recent-login can't recur here —
      // any failure past this point is a genuine save/delete error, not the
      // stale-session case this whole flow exists to prevent.
      setError(userMessage(err, 'Something went wrong deleting your account. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-app-bg/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-white border border-app-border rounded-[32px] w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl relative overflow-hidden"
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-app-border/40 flex items-center justify-between bg-app-bg/50">
          <div>
            <h2 className="text-xl font-serif font-black text-app-text italic">My Profile</h2>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-app-surface-up rounded-full transition-colors">
            <X className="w-5 h-5 text-app-sub" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 no-scrollbar">
          {error && <div role="alert" className="bg-rose-50/50 text-rose-500/90 text-[11px] font-medium px-4 py-3 rounded-xl mb-6 text-center text-balance border border-rose-100/50 leading-relaxed">{error}</div>}
          {success && <div role="status" className="bg-emerald-50/50 text-emerald-600/90 text-[11px] font-medium px-4 py-3 rounded-xl mb-6 text-center text-balance border border-emerald-100/50 leading-relaxed">{success}</div>}

          <div className="space-y-8">
            {/* Profile Photo */}
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className={`w-32 h-32 rounded-full border-2 ${croppedImage ? 'border-solid border-gold/20' : 'border-dashed border-app-border/40'} overflow-hidden bg-app-faint relative shadow-inner`}>
                  {croppedImage ? (
                    <img src={croppedImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <UserIcon className="w-12 h-12 text-app-sub/20" />
                    </div>
                  )}
                </div>
                
                <button aria-label="Change photo" 
                  onClick={() => setShowImageMenu(!showImageMenu)}
                  className="absolute bottom-0 right-0 w-10 h-10 bg-gold text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 active:scale-95 transition z-20 border-4 border-white"
                >
                  <Edit2 className="w-4 h-4" />
                </button>

                <AnimatePresence>
                  {showImageMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: 10 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-44 bg-white border border-app-border shadow-2xl rounded-2xl p-1.5 z-[100]"
                    >
                      <label className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-app-faint transition-colors text-left cursor-pointer group/item">
                        <Camera className="w-4 h-4 text-app-sub group-hover/item:text-gold" />
                        <span className="text-xs font-semibold text-app-sub group-hover/item:text-app-text">Change Photo</span>
                        <input type="file" value="" className="hidden" accept="image/*" onChange={(e) => { handleImageUpload(e); setShowImageMenu(false); }} />
                      </label>
                      {croppedImage && (
                        <button 
                          onClick={() => { setCroppedImage(null); setShowImageMenu(false); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-rose-50 transition-colors text-left group/item text-rose-500"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="text-xs font-bold">Delete Photo</span>
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Inputs */}
            <div className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-[9px] font-black uppercase text-app-sub opacity-40 tracking-widest block pl-1">Full Name</label>
                <div className="relative">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-app-sub opacity-30" />
                  <input 
                    value={name} 
                    maxLength={100}
                    onChange={e => {
                      setName(e.target.value);
                      if (showValidationNames.includes('name')) setShowValidationNames(prev => prev.filter(n => n !== 'name'));
                    }}
                    placeholder="Your name"
                    className={`w-full bg-white border ${showValidationNames.includes('name') ? 'border-rose-500 ring-1 ring-rose-500' : 'border-app-border/60'} rounded-2xl pl-11 pr-5 py-4 text-sm font-bold text-app-text outline-none focus:border-gold/50 shadow-sm transition-all`}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[9px] font-black uppercase text-app-sub opacity-40 tracking-widest block pl-1">Home City</label>
                <LocationSearch
                  value={homeLocation}
                  onChange={v => {
                    setHomeLocation(v);
                    if (showValidationNames.includes('location')) setShowValidationNames(prev => prev.filter(n => n !== 'location'));
                  }}
                  placeholder="Search city..."
                  error={showValidationNames.includes('location')}
                />
              </div>

              <div className="space-y-1.5 opacity-60">
                <label className="text-[9px] font-black uppercase text-app-sub opacity-40 tracking-widest block pl-1">Login Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-app-sub opacity-30" />
                  <input 
                    value={user.email} 
                    readOnly
                    disabled
                    className="w-full bg-app-faint border border-app-border/40 rounded-2xl pl-11 pr-5 py-4 text-sm font-bold text-app-sub outline-none cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-4">
              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="w-full bg-app-text text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl hover:brightness-110 active:scale-[0.98] transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {/* Icon leads, as on every other button on this screen. */}
                <Save className="w-4 h-4" /> {loading ? 'Saving Changes...' : 'Save Changes'}
              </button>

              {showPasswordConfirm ? (
                <div className="bg-gold/5 p-5 rounded-2xl border border-gold/10 space-y-4 animate-fade-up">
                  <p className="text-xs text-gold font-medium text-center text-balance leading-relaxed">
                    A password reset link will be sent to your email. You will need to log in again with your new password.
                  </p>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => setShowPasswordConfirm(false)}
                      className="flex-1 bg-white border border-gold/20 text-gold py-3 rounded-xl text-[9px] font-bold uppercase tracking-widest"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => { handlePasswordReset(); setShowPasswordConfirm(false); }}
                      disabled={loading}
                      className="flex-1 bg-gold text-white py-3 rounded-xl text-[9px] font-bold uppercase tracking-widest shadow-lg shadow-gold/20"
                    >
                      Send Reset Link
                    </button>
                  </div>
                </div>
              ) : (
                <button 
                  onClick={() => setShowPasswordConfirm(true)}
                  disabled={loading}
                  className="w-full bg-gold/10 text-gold py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-gold/20 active:scale-[0.98] transition flex items-center justify-center gap-2"
                >
                  <Key className="w-4 h-4" /> Change Password
                </button>
              )}
            </div>

            {/* Danger Zone */}
            <div className="pt-8 border-t border-app-border/40">
              {awaitingDeletePassword ? (
                <div className="bg-rose-50 p-6 rounded-2xl border border-rose-100 space-y-4">
                  <p className="text-sm text-rose-700 font-medium text-center text-balance leading-relaxed">
                    Confirm your password to permanently delete your account. Nothing is deleted until this succeeds.
                  </p>
                  <input
                    type="password"
                    autoFocus
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && deletePassword && !loading) handleDeleteAccount(); }}
                    placeholder="Your password"
                    className={`${FIELD_AUTH} px-5 ${fieldBorder(false, true)}`}
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setAwaitingDeletePassword(false); setShowConfirmDelete(false); setDeletePassword(''); setError(null); }}
                      disabled={loading}
                      className="flex-1 bg-white border border-rose-200 text-rose-500 py-3 rounded-xl text-[9px] font-bold uppercase tracking-widest"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={loading || !deletePassword}
                      className="flex-1 bg-rose-500 text-white py-3 rounded-xl text-[9px] font-bold uppercase tracking-widest shadow-lg shadow-rose-200 disabled:opacity-50"
                    >
                      {loading ? 'Deleting…' : 'Confirm & Delete Forever'}
                    </button>
                  </div>
                </div>
              ) : showConfirmDelete ? (
                <div className="bg-rose-50 p-6 rounded-2xl border border-rose-100 space-y-4">
                  <p className="text-sm text-rose-700 font-medium text-center text-balance leading-relaxed">
                    Are you sure you want to delete your account? This will permanently erase your profile data, notifications and trips created by you. This cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowConfirmDelete(false)}
                      className="flex-1 bg-white border border-rose-200 text-rose-500 py-3 rounded-xl text-[9px] font-bold uppercase tracking-widest"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setAwaitingDeletePassword(true)}
                      className="flex-1 bg-rose-500 text-white py-3 rounded-xl text-[9px] font-bold uppercase tracking-widest shadow-lg shadow-rose-200"
                    >
                      Delete Forever
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowConfirmDelete(true)}
                  className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl border-2 border-rose-50 text-rose-500 hover:bg-rose-50 transition text-[10px] font-black uppercase tracking-widest"
                >
                  <UserX className="w-4 h-4" /> Delete Account
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Cropper Overlay */}
        <AnimatePresence>
          {isCropping && image && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-[200] bg-black/90 flex flex-col p-6"
            >
              <div className="flex-1 relative mt-10 mb-20">
                <LazyCropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>
              
              <div className="space-y-6">
                <input type="range" value={zoom} min={1} max={3} step={0.1} onChange={(e) => setZoom(Number(e.target.value))} className="w-full accent-gold" />
                <div className="flex gap-4">
                  <button onClick={() => setIsCropping(false)} className="flex-1 bg-white/10 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-widest">Cancel</button>
                  <button onClick={showCroppedImage} className="flex-1 bg-gold text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-widest">Apply</button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}
